import type { AppUser } from '../stores/authStore';

// ─── types ───────────────────────────────────────────────────────────────────
interface EducationEntry {
  id: string;
  type: 'COLLEGE' | 'GRADUATE';
  year: string;
  schoolName: string;
  major: string;
  status: 'GRADUATED' | 'COMPLETED' | 'ENROLLED';
  degreeLevel?: 'MASTERS' | 'DOCTORATE';
  degreeReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
}
interface ExperienceEntry {
  id: string;
  year: string;
  company: string;
  position: string;
}
interface AwardEntry {
  id: string;
  year: string;
  title: string;
}
interface CertificateEntry {
  id: string;
  name: string;
  issuer: string;
  date: string;
  reviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED' | null;
}
interface Visibility {
  education: boolean;
  experience: boolean;
  awards: boolean;
  certificates_list: boolean;
  introduction: boolean;
  stampfit_certificates: boolean;
  portfolio: boolean;
}

export interface ResumeTemplate2Props {
  user: AppUser | null;
  photoUrl: string | null;
  education: EducationEntry[];
  experience: ExperienceEntry[];
  awards: AwardEntry[];
  certificatesList: CertificateEntry[];
  stampfitCerts: any[];
  introduction: string;
  visibility: Visibility;
  desiredPosition: string;
  address: string;
  getPortfolioDisplayUrl: (slot: number) => string;
  onPhotoClick?: (url: string) => void;
}

// ─── Layout constants ─────────────────────────────────────────────────────────
const PAGE_W = 794;
const PAGE_H = 1123;
const LEFT_W = 263;
const RIGHT_W = PAGE_W - LEFT_W; // 531
const NOTO = "'Noto Sans KR', sans-serif";
const GRAY_BG = '#F2F2F2';
const PHOTO_H = 395; // 104:192 ratio → 1123 × 104/296 ≈ 395

// Left column inner padding
const L_PAD = 20;

// Right column content padding
const RP_L = 32;
const RP_R = 24;
const R_INNER = RIGHT_W - RP_L - RP_R; // 475

// Portfolio images
const PORT_GAP = 14;
const PORT_SQ = Math.floor((R_INNER - PORT_GAP * 2) / 3); // 149

// Thick bar width — roughly 44% of inner width
const BAR_W = Math.round(R_INNER * 0.44); // ~209px

// Date column width in two-column rows
const DATE_COL_W = 130;

// Max items per section
const MAX_EXP = 9;
const MAX_CERT = 6;
const MAX_SF = 6;
const MAX_AWARD = 9;

// Page 1 right column fixed Y positions
const P1_INTRO = 22;
const P1_BAR1 = 144;
const P1_PORT_T = 159;
const P1_PORT_C = 192;
// BAR2 = PORT_C + PORT_SQ + 15 = 192 + 149 + 15 = 356
const P1_BAR2 = 356;
const P1_EDU_T = 372;
const P1_EDU_C = 410;
const P1_BAR3 = 700;
const P1_EXP_T = 715;
const P1_EXP_C = 752;

// Page 2 right column fixed Y positions
const P2_BAR1 = 24;
const P2_CERT_T = 42;
const P2_CERT_C = 86;
const P2_BAR2 = 418;
const P2_SF_T = 436;
const P2_SF_C = 480;
const P2_BAR3 = 814;
const P2_AWARD_T = 832;
const P2_AWARD_C = 870;

// ─── helpers ─────────────────────────────────────────────────────────────────
const STATUS_LABEL: Record<string, string> = {
  GRADUATED: '졸업', COMPLETED: '수료', ENROLLED: '재학',
};
const DEGREE_LABEL: Record<string, string> = {
  MASTERS: '석사', DOCTORATE: '박사',
};

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .trim();
}

function formatPhone(phone?: string): string {
  if (!phone) return '';
  const d = phone.replace(/\D/g, '');
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6)}`;
  return phone;
}

function formatBirthDate(date?: string): string {
  if (!date) return '';
  return date.replace(/-/g, '.');
}

function certDate(date: string): string {
  if (!date) return '';
  const parts = date.split('-');
  if (parts.length === 3) {
    return `${parts[0]}. ${parseInt(parts[1], 10)}. ${parseInt(parts[2], 10)}`;
  }
  return date.replace(/-/g, '. ');
}

// ─── sub-components ──────────────────────────────────────────────────────────

function Badge2({
  label = '인증',
  variant = 'default',
}: {
  label?: string;
  variant?: 'default' | 'degree' | 'warn';
}) {
  const isDegree = variant === 'degree';
  const isWarn = variant === 'warn';
  const bg = isDegree ? '#eaf7ec' : isWarn ? '#fff8e1' : '#F5821F';
  const color = isDegree ? '#236e2b' : isWarn ? '#b45309' : '#fff';
  const border = isDegree ? '1px solid #a3d9a5' : isWarn ? '1px solid #fcd34d' : 'none';
  const br = isDegree ? 20 : 3;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: bg, color,
      fontSize: 10, fontWeight: 700,
      padding: isDegree ? '3px 9px 3px 6px' : '2px 7px 2px 5px',
      borderRadius: br, border,
      verticalAlign: 'middle', lineHeight: 1.2,
      fontFamily: NOTO, flexShrink: 0, whiteSpace: 'nowrap',
    }}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
        {isDegree ? (
          <>
            <circle cx="5" cy="5" r="4.2" stroke="#236e2b" strokeWidth="1.2" fill="none" />
            <path d="M2.8 5.1L4.2 6.5L7.2 3.5" stroke="#236e2b" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </>
        ) : isWarn ? (
          <>
            <path d="M5 2L8.5 8H1.5L5 2Z" stroke="#b45309" strokeWidth="1.2" fill="none" strokeLinejoin="round" />
            <line x1="5" y1="4.5" x2="5" y2="6.2" stroke="#b45309" strokeWidth="1.1" strokeLinecap="round" />
            <circle cx="5" cy="7.2" r="0.5" fill="#b45309" />
          </>
        ) : (
          <path d="M1.5 4.5L3.5 6.5L7.5 2.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        )}
      </svg>
      {label}
    </span>
  );
}

function ThickBar({ top }: { top: number }) {
  return (
    <div style={{
      position: 'absolute', top, left: RP_L,
      width: BAR_W, height: 4, background: '#111',
    }} />
  );
}

function SecTitle({ top, text }: { top: number; text: string }) {
  return (
    <div style={{
      position: 'absolute', top, left: RP_L, right: RP_R,
      fontSize: 28, fontWeight: 100, color: '#111',
      fontFamily: NOTO, lineHeight: 1,
    }}>
      {text}
    </div>
  );
}

// ─── Left column (same on both pages) ────────────────────────────────────────

function LeftCol2({
  user, photoUrl, desiredPosition, address,
}: {
  user: AppUser | null;
  photoUrl: string | null;
  desiredPosition: string;
  address: string;
}) {
  const nameEn = (user?.name_en || '').toUpperCase();
  const birthDate = formatBirthDate(user?.birth_date);
  const phone = formatPhone(user?.phone);
  const email = user?.email || '';

  const infoItems = [
    ...(birthDate ? [{ label: 'Date of Birth', value: birthDate }] : []),
    ...(phone ? [{ label: 'Phone', value: phone }] : []),
    ...(address ? [{ label: 'Address', value: address }] : []),
    ...(email ? [{ label: 'E-mail', value: email }] : []),
  ];

  return (
    <div style={{
      position: 'absolute', top: 0, left: 0,
      width: LEFT_W, height: PAGE_H,
      background: GRAY_BG, fontFamily: NOTO, overflow: 'hidden',
    }}>
      {/* Photo — full-width rectangle */}
      <div style={{
        position: 'absolute', top: 0, left: 0,
        width: LEFT_W, height: PHOTO_H,
        background: '#D0D0D0', overflow: 'hidden',
      }}>
        {photoUrl && (
          <img
            src={photoUrl}
            alt="프로필"
            style={{
              width: '100%', height: '100%',
              objectFit: 'cover', objectPosition: 'center top',
              display: 'block',
            }}
          />
        )}
      </div>

      {/* English name */}
      {nameEn && (
        <div style={{
          position: 'absolute',
          top: PHOTO_H + 20,
          left: L_PAD, right: L_PAD,
          fontSize: 32, fontWeight: 100, color: '#111',
          lineHeight: 1.15, letterSpacing: 0.3,
          wordBreak: 'break-word',
        }}>
          {nameEn}
        </div>
      )}

      {/* Desired position */}
      {desiredPosition && (
        <div style={{
          position: 'absolute',
          top: PHOTO_H + 65,
          left: L_PAD, right: L_PAD,
          fontSize: 16, fontWeight: 100, color: '#2a2a2a', lineHeight: 1.3,
        }}>
          {desiredPosition}
        </div>
      )}

      {/* Info items — flex column so long values don't overlap next item */}
      <div style={{
        position: 'absolute',
        top: PHOTO_H + 108,
        left: L_PAD, right: L_PAD,
        display: 'flex', flexDirection: 'column', gap: 22,
      }}>
        {infoItems.map(item => (
          <div key={item.label}>
            <div style={{ fontSize: 16, fontWeight: 100, color: '#1a1a1a', lineHeight: 1.3 }}>
              {item.label}
            </div>
            <div style={{
              fontSize: 15, fontWeight: 100, color: '#333',
              lineHeight: 1.55, marginTop: 4, wordBreak: 'break-word',
            }}>
              · {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Page 1 right column ──────────────────────────────────────────────────────

function Page1Right({
  introduction, education, experience, visibility, getPortfolioDisplayUrl, onPhotoClick,
}: Pick<ResumeTemplate2Props, 'introduction' | 'education' | 'experience' | 'visibility' | 'getPortfolioDisplayUrl' | 'onPhotoClick'>) {
  const introText = stripHtml(introduction).trim();

  const colleges = education.filter(e => e.type === 'COLLEGE');
  const graduates = education.filter(e => e.type === 'GRADUATE');
  const experiences = experience.slice(0, MAX_EXP);

  return (
    <div style={{
      position: 'absolute', top: 0, left: LEFT_W,
      width: RIGHT_W, height: PAGE_H,
      background: '#fff', fontFamily: NOTO, overflow: 'hidden',
    }}>
      {/* ── Self-intro ── */}
      {visibility.introduction && (
        <div style={{
          position: 'absolute',
          top: P1_INTRO, left: RP_L, right: RP_R,
          height: P1_BAR1 - P1_INTRO - 6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <p style={{
            fontFamily: NOTO, fontSize: 17, fontWeight: 100,
            color: '#333', textAlign: 'center', lineHeight: 1.8,
            margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>
            {introText
              ? `“ ${introText} ”`
              : '“ 자기소개를 입력해주세요 ”'}
          </p>
        </div>
      )}

      <ThickBar top={P1_BAR1} />

      {/* ── Portfolio ── */}
      {visibility.portfolio && (
        <>
          <SecTitle top={P1_PORT_T} text="포트폴리오" />
          <div style={{
            position: 'absolute', top: P1_PORT_C, left: RP_L,
            display: 'flex', gap: PORT_GAP,
          }}>
            {[0, 1, 2].map(slot => {
              const url = getPortfolioDisplayUrl(slot);
              return (
                <div key={slot} style={{
                  width: PORT_SQ, height: PORT_SQ,
                  background: '#1a1a2e', overflow: 'hidden', flexShrink: 0,
                  cursor: url && onPhotoClick ? 'pointer' : 'default',
                }}
                  onClick={() => url && onPhotoClick?.(url)}
                >
                  {url && (
                    <img
                      src={url}
                      alt={`포트폴리오 ${slot + 1}`}
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'center',
                        display: 'block',
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <ThickBar top={P1_BAR2} />

      {/* ── Education ── */}
      {visibility.education && (
        <>
          <SecTitle top={P1_EDU_T} text="학력" />
          <div style={{
            position: 'absolute',
            top: P1_EDU_C, left: RP_L, right: RP_R,
            height: P1_BAR3 - P1_EDU_C - 8,
            overflow: 'hidden', fontFamily: NOTO,
          }}>
            {colleges.length > 0 && (
              <div>
                <div style={{ fontSize: 17, fontWeight: 100, color: '#111', marginBottom: 10 }}>
                  대학교
                </div>
                {colleges.map((edu, i) => (
                  <div key={edu.id} style={{ marginBottom: i < colleges.length - 1 ? 16 : 20 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: 8, flexWrap: 'wrap',
                    }}>
                      <span style={{ fontSize: 15, color: '#444', fontWeight: 100, minWidth: 40 }}>
                        {edu.year}
                      </span>
                      <span style={{ fontSize: 15, color: '#111', fontWeight: 100 }}>
                        {edu.schoolName}
                      </span>
                      {edu.degreeReviewStatus === 'APPROVED' && (
                        <Badge2 label="학위 인증 완료" variant="degree" />
                      )}
                    </div>
                    <div style={{ fontSize: 14, color: '#555', fontWeight: 100, marginTop: 4, marginLeft: 50 }}>
                      {edu.major} {STATUS_LABEL[edu.status] || edu.status}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {graduates.length > 0 && (
              <div style={{ marginTop: colleges.length > 0 ? 12 : 0 }}>
                <div style={{ fontSize: 17, fontWeight: 100, color: '#111', marginBottom: 10 }}>
                  대학원
                </div>
                {graduates.map((edu, i) => (
                  <div key={edu.id} style={{ marginBottom: i < graduates.length - 1 ? 16 : 0 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center',
                      gap: 8, flexWrap: 'wrap',
                    }}>
                      <span style={{ fontSize: 15, color: '#444', fontWeight: 100, minWidth: 40 }}>
                        {edu.year}
                      </span>
                      <span style={{ fontSize: 15, color: '#111', fontWeight: 100 }}>
                        {edu.schoolName}
                      </span>
                      {edu.degreeReviewStatus === 'APPROVED' && (
                        <Badge2 label="학위 인증 완료" variant="degree" />
                      )}
                    </div>
                    <div style={{ fontSize: 14, color: '#555', fontWeight: 100, marginTop: 4, marginLeft: 50 }}>
                      {edu.major}
                      {edu.degreeLevel ? ` ${DEGREE_LABEL[edu.degreeLevel]}` : ''}
                      {' '}{STATUS_LABEL[edu.status] || edu.status}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <ThickBar top={P1_BAR3} />

      {/* ── Experience ── */}
      {visibility.experience && (
        <>
          <SecTitle top={P1_EXP_T} text="경력" />
          <div style={{
            position: 'absolute',
            top: P1_EXP_C, left: RP_L, right: RP_R,
            height: PAGE_H - P1_EXP_C - 16,
            overflow: 'hidden', fontFamily: NOTO,
          }}>
            {experiences.map(exp => (
              <div key={exp.id} style={{
                display: 'flex', alignItems: 'flex-start',
                gap: 0, marginBottom: 11,
              }}>
                <span style={{
                  fontSize: 15, color: '#555', fontWeight: 100,
                  width: DATE_COL_W, flexShrink: 0, lineHeight: 1.6,
                  paddingTop: 1,
                }}>
                  {exp.year}
                </span>
                <span style={{
                  fontSize: 15, color: '#111', fontWeight: 100, lineHeight: 1.6,
                }}>
                  {exp.company}{exp.position ? ` ${exp.position}` : ''}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Page 2 right column ──────────────────────────────────────────────────────

function Page2Right({
  certificatesList, stampfitCerts, awards, visibility,
}: Pick<ResumeTemplate2Props, 'certificatesList' | 'stampfitCerts' | 'awards' | 'visibility'>) {
  const certs = certificatesList.slice(0, MAX_CERT);
  const sfCerts = stampfitCerts.slice(0, MAX_SF);
  const awardList = awards.slice(0, MAX_AWARD);

  return (
    <div style={{
      position: 'absolute', top: 0, left: LEFT_W,
      width: RIGHT_W, height: PAGE_H,
      background: '#fff', fontFamily: NOTO, overflow: 'hidden',
    }}>
      <ThickBar top={P2_BAR1} />

      {/* ── 보유 자격증 및 수료 내역 ── */}
      {visibility.certificates_list && (
        <>
          <SecTitle top={P2_CERT_T} text="보유 자격증 및 수료 내역" />
          <div style={{
            position: 'absolute',
            top: P2_CERT_C, left: RP_L, right: RP_R,
            height: P2_BAR2 - P2_CERT_C - 8,
            overflow: 'hidden', fontFamily: NOTO,
          }}>
            {certs.map(cert => {
              const approved = cert.reviewStatus === 'APPROVED';
              return (
                <div key={cert.id} style={{ marginBottom: 14 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    flexWrap: 'nowrap', gap: 0,
                  }}>
                    <span style={{
                      fontSize: 15, color: '#555', fontWeight: 100,
                      width: DATE_COL_W, flexShrink: 0, paddingTop: 1,
                    }}>
                      {certDate(cert.date)}
                    </span>
                    <span style={{
                      fontSize: 15, color: '#111', fontWeight: 100, marginRight: 8,
                    }}>
                      {cert.name}
                    </span>
                    <Badge2
                      label={approved ? '인증' : '미인증'}
                      variant={approved ? 'default' : 'warn'}
                    />
                  </div>
                  {cert.issuer && (
                    <div style={{
                      fontSize: 14, color: '#777', fontWeight: 100,
                      marginTop: 4, marginLeft: DATE_COL_W,
                    }}>
                      {cert.issuer}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <ThickBar top={P2_BAR2} />

      {/* ── STAMP FIT 인증 교육 내역 ── */}
      {visibility.stampfit_certificates && (
        <>
          <SecTitle top={P2_SF_T} text="STAMP FIT 인증 교육 내역" />
          <div style={{
            position: 'absolute',
            top: P2_SF_C, left: RP_L, right: RP_R,
            height: P2_BAR3 - P2_SF_C - 8,
            overflow: 'hidden', fontFamily: NOTO,
          }}>
            {sfCerts.map((cert: any) => {
              const issued = cert.issued_at
                ? new Date(cert.issued_at)
                    .toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' })
                    .replace(/\. /g, '.')
                    .replace(/\.$/, '')
                : '';
              const provider = cert.provider?.company_name || '';
              const hours = [
                cert.class?.offline_hours > 0 ? `오프라인 ${cert.class.offline_hours}h` : null,
                cert.class?.online_hours > 0 ? `온라인 ${cert.class.online_hours}h` : null,
              ].filter(Boolean).join(' / ');
              return (
                <div key={cert.id} style={{ marginBottom: 14 }}>
                  <div style={{
                    display: 'flex', alignItems: 'center',
                    flexWrap: 'nowrap', gap: 0,
                  }}>
                    <span style={{
                      fontSize: 15, color: '#555', fontWeight: 100,
                      width: DATE_COL_W, flexShrink: 0, paddingTop: 1,
                    }}>
                      {issued}
                    </span>
                    <span style={{
                      fontSize: 15, color: '#111', fontWeight: 100, marginRight: 8,
                    }}>
                      {cert.class_name}
                    </span>
                    <Badge2 label="인증" variant="default" />
                  </div>
                  {(provider || hours) && (
                    <div style={{
                      fontSize: 14, color: '#777', fontWeight: 100,
                      marginTop: 4, marginLeft: DATE_COL_W,
                    }}>
                      {provider}{provider && hours ? '·' : ''}{hours}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <ThickBar top={P2_BAR3} />

      {/* ── 수상 내역 및 위촉 사항 ── */}
      {visibility.awards && (
        <>
          <SecTitle top={P2_AWARD_T} text="수상 내역 및 위촉 사항" />
          <div style={{
            position: 'absolute',
            top: P2_AWARD_C, left: RP_L, right: RP_R,
            height: PAGE_H - P2_AWARD_C - 16,
            overflow: 'hidden', fontFamily: NOTO,
          }}>
            {awardList.map(award => (
              <div key={award.id} style={{
                display: 'flex', alignItems: 'flex-start',
                gap: 0, marginBottom: 11,
              }}>
                <span style={{
                  fontSize: 15, color: '#555', fontWeight: 100,
                  width: DATE_COL_W, flexShrink: 0, lineHeight: 1.6, paddingTop: 1,
                }}>
                  {award.year}
                </span>
                <span style={{ fontSize: 15, color: '#111', fontWeight: 100, lineHeight: 1.6 }}>
                  {award.title}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────
function PageWrap({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="resume-page"
      style={{
        width: PAGE_W, height: PAGE_H,
        position: 'relative', overflow: 'hidden',
        background: '#fff',
      }}
    >
      {children}
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────
export default function ResumeTemplate2Preview(props: ResumeTemplate2Props) {
  const { user, photoUrl, desiredPosition, address } = props;
  const leftProps = { user, photoUrl, desiredPosition, address };

  return (
    <div className="resume-template-root" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <PageWrap>
        <LeftCol2 {...leftProps} />
        <Page1Right {...props} />
      </PageWrap>
      <PageWrap>
        <LeftCol2 {...leftProps} />
        <Page2Right {...props} />
      </PageWrap>
    </div>
  );
}
